import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import type { FeedingFeedCoverage } from '@/domain/feedingDashboard';

import { dashboardLocale } from '../dashboardLocale';
import { FeedingKpiCard } from '../FeedingKpiCard/FeedingKpiCard';

import { formatNumber, headlinePercent, type KpiCardProps } from './kpiCardFormat';
import { coverageNote } from './kpiCoverageNote';

/**
 * IPS 34 keeps the literal formula, so a day without a plan still counts its actual feed
 * against no plan and raises the figure. Saying how many days carry each total is what
 * lets a reader tell a real overfeed from a plan gap. One line per total.
 */
function dayCoverage(coverage: FeedingFeedCoverage | null, t: TFunction): string[] | undefined {
    if (!coverage || coverage.days === 0) return undefined;
    const perPenDay = coverage.pens > 1;
    const notes: string[] = [];
    if (coverage.actualDays < coverage.days) {
        notes.push(
            t(
                perPenDay
                    ? 'feedingModule.kpi.feedActualPenDays'
                    : 'feedingModule.kpi.feedActualDays',
                { covered: coverage.actualDays, count: coverage.days }
            )
        );
    }
    if (coverage.planDays < coverage.days) {
        notes.push(
            t(perPenDay ? 'feedingModule.kpi.feedPlanPenDays' : 'feedingModule.kpi.feedPlanDays', {
                covered: coverage.planDays,
                count: coverage.days,
            })
        );
    }
    return notes.length > 0 ? notes : undefined;
}

export function FeedVsPlanKpiCard(props: KpiCardProps) {
    const { summary } = props;
    const { t, i18n } = useTranslation();
    const locale = dashboardLocale(i18n);
    const { noteReasons } = coverageNote(
        [
            { metric: 'feedActual', label: t('feedingModule.charts.feedActual') },
            { metric: 'feedPlan', label: t('feedingModule.charts.feedPlan') },
        ],
        props,
        t
    );
    const note = dayCoverage(summary.feedCoverage, t);
    const tonnes = (kg: number | null): string =>
        formatNumber(kg == null ? null : kg / 1000, 1, locale, 't');

    return (
        <FeedingKpiCard
            label={t('feedingModule.kpi.feedVsPlan')}
            values={[headlinePercent(summary.feedVsPlanPct, 0, { locale, signed: true })]}
            details={[
                {
                    label: t('feedingModule.charts.feedActual'),
                    value: tonnes(summary.feedActualKg),
                },
                { label: t('feedingModule.charts.feedPlan'), value: tonnes(summary.feedPlannedKg) },
            ]}
            note={note}
            noteReasons={note ? noteReasons : undefined}
        />
    );
}
